import React, { useState, useEffect } from 'react';
import { masterSupabase } from "../../lib/supabase-master.js";
import { 
  FileText, 
  Users, 
  CreditCard, 
  Wrench, 
  MessageSquare, 
  Clock,
  ChevronDown,
  ChevronUp,
  Filter,
  MoreHorizontal,
  Home,
  UserPlus,
  Upload,
  Trash2,
  Eye,
  Download,
  Edit,
  Key,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Settings,
  RefreshCw,
  DollarSign,
  Building2,
  User
} from 'lucide-react';
import { propertiesService } from '../../services/propertiesService';
import documentsService from '../../services/documentsService';

/**
 * UnifiedActivityFeed - Einheitliche Activity-Komponente für Dashboard und Tasks
 * 
 * Props:
 * - mode: 'dashboard' | 'full' - Anzeigemodus
 * - showFilter: boolean - Filter-Dropdown anzeigen
 * - maxItems: number - Maximale Anzahl Items (nur dashboard mode)
 * - autoRefresh: boolean - Auto-Refresh aktivieren
 * - refreshInterval: number - Refresh-Intervall in Sekunden
 */
const UnifiedActivityFeed = ({ 
  mode = 'full',
  showFilter = true,
  maxItems = 5,
  autoRefresh = true,
  refreshInterval = 30
}) => {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('Alle');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const filters = ['Alle', 'Dokumente', 'Wartung', 'Zahlungen', 'Verträge', 'Kommunikation', 'System'];

  // Navigation-basierte Aktualisierung statt Timer
  useEffect(() => {
    // Aktualisierung bei Page Focus (Tab-Wechsel)
    const handleFocus = () => {
      loadRealActivities();
    };

    // Aktualisierung bei Visibility Change (Browser-Tab aktiv)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadRealActivities();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    loadRealActivities();
    
    // Echtzeit-Updates für Aktivitäten einrichten
    const setupRealtimeSubscriptions = async () => {
      // Dokumente-Kanal für Echtzeit-Updates
      const documentsChannel = masterSupabase
        .channel('documents-changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'documents' 
        }, (payload) => {
          console.log('📄 Dokument-Änderung erkannt:', payload);
          loadRealActivities(); // Aktivitäten neu laden
        })
        .subscribe();

      // Properties-Kanal
      const propertiesChannel = masterSupabase
        .channel('properties-changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'properties' 
        }, (payload) => {
          console.log('🏠 Immobilien-Änderung erkannt:', payload);
          loadRealActivities();
        })
        .subscribe();

      // Tenants-Kanal
      const tenantsChannel = masterSupabase
        .channel('tenants-changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'tenants' 
        }, (payload) => {
          console.log('👥 Mieter-Änderung erkannt:', payload);
          loadRealActivities();
        })
        .subscribe();

      return () => {
        documentsChannel.unsubscribe();
        propertiesChannel.unsubscribe();
        tenantsChannel.unsubscribe();
      };
    };

    setupRealtimeSubscriptions();
  }, []);

  const loadRealActivities = async () => {
    try {
      setIsLoading(true);
      const allActivities = [];

      // 0. Neue Activities-Tabelle laden (höchste Priorität)
      try {
        const { data: activities } = await masterSupabase
          .from('activities')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);

        if (activities && activities.length > 0) {
          activities.forEach(activity => {
            const iconMap = {
              'bewirtschaftung': DollarSign,
              'objekte': Home,
              'einheiten': Building2,
              'mieter': Users,
              'eigentümer': User,
              'finanzierungen': CreditCard,
              'dokumente': FileText,
              'document_upload': Upload,
              'property_created': Home,
              'cost_created': DollarSign,
              'cost_updated': DollarSign,
              'cost_deleted': DollarSign,
              'tenant_created': Users,
              'tenant_updated': Users,
              'tenant_deleted': Users
            };

            allActivities.push({
              id: `activity-${activity.id}`,
              type: activity.category || 'System',
              icon: iconMap[activity.activity_type] || Activity,
              title: activity.title,
              description: activity.description,
              time: new Date(activity.created_at),
              user: activity.user_name || 'System',
              metadata: activity.metadata && typeof activity.metadata === 'object' ? activity.metadata : {}
            });
          });
        }
      } catch (error) {
        console.log('Activities-Tabelle nicht verfügbar, verwende Legacy-Daten:', error);
      }

      // 1. Dokumente laden
      try {
        const { data: documents } = await masterSupabase
          .from('documents')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(20);

        if (documents && documents.length > 0) {
          documents.forEach(doc => {
            allActivities.push({
              id: `doc-${doc.id}`,
              type: 'Dokumente',
              icon: Upload,
              title: 'Dokument hochgeladen',
              description: `${doc.filename || 'Neue Datei'} wurde hochgeladen`,
              time: new Date(doc.created_at),
              user: doc.uploaded_by || 'System',
              metadata: {
                filename: doc.filename,
                size: doc.file_size,
                type: doc.file_type
              }
            });
          });
        }
      } catch (error) {
        console.error('Fehler beim Laden der Dokumente:', error);
      }

      // 2. Immobilien laden
      try {
        const { data: properties } = await masterSupabase
          .from('properties')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(10);

        if (properties && properties.length > 0) {
          properties.forEach(property => {
            allActivities.push({
              id: `property-${property.id}`,
              type: 'Objekte',
              icon: Home,
              title: 'Neue Immobilie erstellt',
              description: `${property.street || 'Immobilie'} wurde hinzugefügt`,
              time: new Date(property.created_at),
              user: property.created_by || 'MetaGovernor',
              metadata: {
                street: property.street,
                city: property.city,
                units: property.units
              }
            });
          });
        }
      } catch (error) {
        console.error('Fehler beim Laden der Immobilien:', error);
      }

      // 3. Mieter laden
      try {
        const { data: tenants } = await masterSupabase
          .from('tenants')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(15);

        if (tenants && tenants.length > 0) {
          tenants.forEach(tenant => {
            const name = tenant.name || 'Neuer Mieter';
            allActivities.push({
              id: `tenant-${tenant.id}`,
              type: 'Mieter',
              icon: UserPlus,
              title: 'Neuer Mieter hinzugefügt',
              description: `${name} wurde registriert`,
              time: new Date(tenant.created_at),
              user: 'Sie',
              metadata: {
                name: name,
                email: tenant.email,
                phone: tenant.phone
              }
            });
          });
        }
      } catch (error) {
        console.error('Fehler beim Laden der Mieter:', error);
      }

      // 4. Wartungsaufträge laden (mit Tabellen-Check)
      try {
        // Prüfe erst ob Tabelle existiert
        const { data: tableCheck, error: tableError } = await masterSupabase
          .from('maintenance')
          .select('count', { count: 'exact' });

        if (!tableError) {
          const { data: maintenance } = await masterSupabase
            .from('maintenance')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(10);

          if (maintenance && maintenance.length > 0) {
            maintenance.forEach(request => {
              allActivities.push({
                id: `maintenance-${request.id}`,
                type: 'Wartung',
                icon: Wrench,
                title: 'Wartungsauftrag erstellt',
                description: `${request.title || 'Wartung'} wurde angelegt`,
                time: new Date(request.created_at),
                user: request.created_by || 'System',
                metadata: {
                  title: request.title,
                  priority: request.priority,
                  status: request.status
                }
              });
            });
          }
        }
      } catch (error) {
        // Tabelle existiert nicht - kein Fehler loggen
        console.log('Wartungstabelle nicht verfügbar (normal bei neuen Installationen)');
      }

      // Aktivitäten nach Zeit sortieren
      allActivities.sort((a, b) => new Date(b.time) - new Date(a.time));
      
      setActivities(allActivities);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Fehler beim Laden der Aktivitäten:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Aktivitäten filtern
  useEffect(() => {
    if (selectedFilter === 'Alle') {
      setFilteredActivities(activities);
    } else {
      setFilteredActivities(activities.filter(activity => activity.type === selectedFilter));
    }
  }, [selectedFilter, activities]);

  // Angezeigte Aktivitäten basierend auf Modus
  const getDisplayedActivities = () => {
    if (mode === 'dashboard') {
      return filteredActivities.slice(0, 3); // Nur 3 Items für Dashboard
    }
    return isExpanded ? filteredActivities : filteredActivities.slice(0, 5);
  };

  const displayedActivities = getDisplayedActivities();

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'vor wenigen Sekunden';
    if (diffInSeconds < 3600) return `vor ${Math.floor(diffInSeconds / 60)} Minute(n)`;
    if (diffInSeconds < 86400) return `vor ${Math.floor(diffInSeconds / 3600)} Stunde(n)`;
    return `vor ${Math.floor(diffInSeconds / 86400)} Tag(en)`;
  };

  const getActivityIcon = (type) => {
    const iconMap = {
      'Dokumente': Upload,
      'Objekte': Home,
      'Mieter': UserPlus,
      'Wartung': Wrench,
      'Zahlungen': CreditCard,
      'Kommunikation': MessageSquare,
      'System': Settings
    };
    return iconMap[type] || FileText;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header für Dashboard Mode */}
      {mode === 'dashboard' && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors" 
               onClick={() => window.location.href = '/activities#activity-log'}>
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Activity Log
            </h3>
          </div>
          <button
            onClick={() => window.location.href = '/activities#activity-log'}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Alle anzeigen
          </button>
        </div>
      )}

      {/* Header mit Filter und Refresh (nur im full mode) */}
      {mode === 'full' && (
        <div className="flex items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Letzte Aktivitäten
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {showFilter && (
              <div className="relative">
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="appearance-none bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {filters.map(filter => (
                    <option key={filter} value={filter}>{filter}</option>
                  ))}
                </select>
                <Filter className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}
            
            <button
              onClick={() => loadRealActivities()}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              title="Aktivitäten aktualisieren"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Aktivitätenliste */}
      <div className="space-y-3">
        {displayedActivities.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>Keine Aktivitäten für "{selectedFilter}" gefunden.</p>
          </div>
        ) : (
          displayedActivities.map(activity => {
            const IconComponent = activity.icon;
            return (
              <div
                key={activity.id}
                className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {activity.title}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTimeAgo(activity.time)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {activity.description}
                  </p>
                  
                  <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full mr-2">
                      {activity.type}
                    </span>
                    <span>von {activity.user}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Counter für weitere Aktivitäten (nur Dashboard) */}
      {mode === 'dashboard' && !isLoading && filteredActivities.length > 3 && (
        <div className="text-center pt-3 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => window.location.href = '/activities#activity-log'}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
          >
            {filteredActivities.length - 3} weitere Aktivitäten anzeigen
          </button>
        </div>
      )}

      {/* Expand Button (nur im full mode) */}
      {mode === 'full' && !isLoading && filteredActivities.length > 5 && (
        <div className="text-center pt-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" />
                Weniger anzeigen
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" />
                {filteredActivities.length - 5} weitere anzeigen
              </>
            )}
          </button>
        </div>
      )}

      {/* Refresh Indicator */}
      {mode === 'full' && (
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center pt-2">
          Letzte Aktualisierung: {lastRefresh.toLocaleTimeString()}
          • Aktualisiert bei Navigation
        </div>
      )}
    </div>
  );
};

export default UnifiedActivityFeed;

